// AES-256-GCM vault primitives on Web Crypto. All ciphertext is base64(nonce‖ct‖tag).
//
// CRITICAL crypto parity: this wire format MUST match the iOS VaultKeys/EntryCrypto
// code — AES-256-GCM, ciphertext = base64(nonce(12) ‖ ciphertext ‖ tag(16)), key
// derivation PBKDF2-SHA256 @ 100k iterations. A DEK wrapped on iOS must unwrap here,
// and vice-versa. Keep both sides in lockstep.
const enc = new TextEncoder();
const dec = new TextDecoder();

function b64(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = ''; for (const b of u8) s += String.fromCharCode(b);
  return btoa(s);
}
function unb64(s: string) {
  // Build via `new Uint8Array(len)` (ArrayBuffer-backed, inferred Uint8Array<ArrayBuffer>)
  // rather than Uint8Array.from / a bare `: Uint8Array` annotation (ArrayBufferLike),
  // so the result is accepted by crypto.subtle's BufferSource parameter.
  const bin = atob(s);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}

export async function generateDEK(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}
export async function exportKeyRaw(key: CryptoKey): Promise<string> {
  return b64(await crypto.subtle.exportKey('raw', key));
}
export async function importDEK(rawB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', unb64(rawB64), { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

export async function sealWithKey(key: CryptoKey, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext));
  const out = new Uint8Array(iv.length + ct.byteLength);
  out.set(iv, 0); out.set(new Uint8Array(ct), iv.length);
  return b64(out);
}
export async function openWithKey(key: CryptoKey, boxB64: string): Promise<string> {
  const raw = unb64(boxB64);
  const iv = raw.slice(0, 12); const ct = raw.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return dec.decode(pt);
}

/**
 * Seal raw bytes — a photo — into the same nonce‖ct‖tag box, but left as bytes rather than base64.
 *
 * Base64 would cost a third more of everything: upload time on a phone tether, R2 storage, and the
 * download before a picture can appear. It is worth it for a text column in D1; it is not worth it
 * for a megabyte of JPEG whose destination is an octet-stream body either way.
 *
 * The layout is byte-identical to `sealWithKey`, which is what lets iOS open one of these with
 * `AES.GCM.SealedBox(combined:)` — CryptoKit's `combined` is exactly nonce(12)‖ciphertext‖tag(16).
 */
export async function sealBytes(key: CryptoKey, plaintext: Uint8Array): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext as BufferSource);
  const out = new Uint8Array(iv.length + ct.byteLength);
  out.set(iv, 0); out.set(new Uint8Array(ct), iv.length);
  return out;
}

/** Open a box produced by `sealBytes`, or by iOS's `AES.GCM.seal(...).combined`. */
export async function openBytes(key: CryptoKey, box: Uint8Array): Promise<Uint8Array> {
  const iv = box.slice(0, 12); const ct = box.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct as BufferSource);
  return new Uint8Array(pt);
}

// KEK derivation (PBKDF2). NOTE: Workers caps PBKDF2 at 100k iterations.
export async function deriveKeyFromSecret(secret: string, salt: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', enc.encode(secret), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(salt), iterations: 100_000, hash: 'SHA-256' },
    material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

// Wrap = seal the DEK's raw bytes with a KEK; reuse the same box format.
export async function wrapDEK(dek: CryptoKey, kek: CryptoKey): Promise<string> {
  return sealWithKey(kek, await exportKeyRaw(dek));
}
export async function unwrapDEK(wrappedB64: string, kek: CryptoKey): Promise<CryptoKey> {
  return importDEK(await openWithKey(kek, wrappedB64));
}
